import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Check, X, QrCode, ExternalLink, Star, Store, Plus, Edit, Database, Trash2, KeyRound, Copy, MessageCircle, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';
import { API, copyText, mgmtShareMsg, waLink } from './smartqrApi';

const statusCls = { pending: 'bg-amber-100 text-amber-700', paid: 'bg-green-100 text-green-700', failed: 'bg-red-100 text-red-700' };
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export const AdminSmartQR = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { setItems((await axios.get(`${API}/api/smart-qr/admin/payments`, authHeaders())).data); } catch { toast.error('Failed to load Smart QR payments'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    if (action === 'activate' && !window.confirm('Confirm you have verified this Payment ID in the Razorpay dashboard (₹99 captured)?')) return;
    try {
      await axios.post(`${API}/api/smart-qr/admin/payments/${id}/${action}`, {}, authHeaders());
      toast.success(action === 'activate' ? 'Premium activated' : 'Request rejected');
      load();
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Action failed'); }
  };

  if (loading) return <div className="p-10 text-center text-slate-500 text-sm">Loading...</div>;
  return (
    <div className="space-y-4">
      <FeaturedShops />
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-smartqr">
      <div className="p-4 border-b border-slate-100 text-xs text-slate-500">Smart Business QR Premium (₹99) payment requests. Verify the Razorpay Payment ID in your Razorpay dashboard before activating.</div>
      {items.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No Smart QR payment requests yet</div> : (
        <div className="divide-y">
          {items.map((p) => (
            <div key={p.payment_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`smartqr-payment-${p.payment_id}`}>
              <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0"><QrCode className="w-5 h-5 text-slate-600" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm">{p.business?.business_name || 'Unknown business'} <span className="text-slate-400 font-normal">· {p.business?.city}</span></p>
                <p className="text-xs text-slate-500">WhatsApp: {p.business?.whatsapp} · Payment ID: <span className="font-mono">{p.payment_reference}</span> · ₹{p.payment_amount}</p>
                <p className="text-xs text-slate-400">{p.reason} · {new Date(p.created_at).toLocaleString()}</p>
                {p.business?.public_slug && <a href={`/b/${p.business.public_slug}`} target="_blank" rel="noreferrer" className="text-xs text-orange-600 inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> View page</a>}
              </div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusCls[p.payment_status] || 'bg-slate-100 text-slate-600'}`} data-testid={`smartqr-status-${p.payment_id}`}>{p.payment_status.toUpperCase()}</span>
              {p.payment_status !== 'paid' && (
                <div className="flex gap-2">
                  <button onClick={() => act(p.payment_id, 'activate')} className="bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1" data-testid={`smartqr-activate-${p.payment_id}`}><Check className="w-3.5 h-3.5" /> Activate</button>
                  {p.payment_status === 'pending' && <button onClick={() => act(p.payment_id, 'reject')} className="bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1" data-testid={`smartqr-reject-${p.payment_id}`}><X className="w-3.5 h-3.5" /> Reject</button>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};

const FeaturedShops = () => {
  const [shops, setShops] = useState([]);
  const load = useCallback(async () => {
    try { setShops((await axios.get(`${API}/api/smart-qr/admin/businesses`, authHeaders())).data); } catch { toast.error('Failed to load Smart QR shops'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (b) => {
    try {
      await axios.post(`${API}/api/smart-qr/admin/businesses/${b.business_id}/featured`, { featured: !b.featured }, authHeaders());
      toast.success(!b.featured ? 'Marked as featured' : 'Removed from featured');
      load();
    } catch { toast.error('Could not update featured status'); }
  };

  const demo = async (action) => {
    if (action === 'remove' && !window.confirm('Remove ALL demo/sample businesses and their products? Genuine businesses are not affected.')) return;
    try {
      const res = action === 'seed' ? await axios.post(`${API}/api/smart-qr/admin/demo/seed`, {}, authHeaders()) : await axios.delete(`${API}/api/smart-qr/admin/demo`, authHeaders());
      toast.success(action === 'seed' ? `Demo seeded: ${res.data.created} created, ${res.data.updated} updated` : `Removed ${res.data.removed} demo businesses`);
      load();
    } catch { toast.error('Demo action failed'); }
  };
  const featuredCount = shops.filter((s) => s.featured).length;
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="admin-smartqr-shops">
      <div className="p-4 border-b border-slate-100 flex items-center justify-between">
        <div><p className="font-semibold text-slate-800 text-sm">Smart QR Shops ({shops.length})</p><p className="text-xs text-slate-500">Featured shops appear first in the homepage NEW ARRIVALS section.</p></div>
        <span className="text-xs font-bold bg-orange-100 text-orange-700 px-2.5 py-1 rounded-full" data-testid="featured-count">{featuredCount} featured</span>
      </div>
      <div className="px-4 py-2 border-b border-slate-100 flex flex-wrap gap-2">
        <Link to="/admin/smart-qr/new" className="inline-flex items-center gap-1 bg-slate-800 text-white px-3 py-1.5 rounded-lg text-xs font-semibold" data-testid="admin-add-business"><Plus className="w-3.5 h-3.5" /> Add business</Link>
        <button onClick={() => demo('seed')} className="inline-flex items-center gap-1 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-lg text-xs font-semibold" data-testid="admin-seed-demo"><Database className="w-3.5 h-3.5" /> Seed 5 demo businesses</button>
        <button onClick={() => demo('remove')} className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg text-xs font-semibold" data-testid="admin-remove-demo"><Trash2 className="w-3.5 h-3.5" /> Remove demo businesses</button>
      </div>
      {shops.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">No Smart QR shops yet</div> : (
        <div className="divide-y max-h-[420px] overflow-y-auto">
          {shops.map((b) => (
            <div key={b.business_id} className="p-3 flex items-center gap-3" data-testid={`shop-${b.public_slug}`}>
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden shrink-0">{b.logo ? <img src={b.logo.startsWith('/') ? `${API}${b.logo}` : b.logo} alt="" className="w-full h-full object-cover" /> : <Store className="w-5 h-5 text-slate-400" />}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 text-sm truncate">{b.business_name} <span className="text-slate-400 font-normal">· {b.category}</span>{b.is_demo && <span className="ml-1 bg-amber-400 text-slate-900 text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle" data-testid={`shop-demo-${b.public_slug}`}>DEMO</span>}{b.disabled && <span className="ml-1 bg-slate-300 text-slate-700 text-[9px] font-bold px-1.5 py-0.5 rounded-full align-middle">UNPUBLISHED</span>}</p>
                <p className="text-xs text-slate-500 truncate">{[b.area, b.city, b.state].filter(Boolean).join(', ')} · {b.plan === 'premium' ? 'Premium' : 'Free'} · <a href={`/b/${b.public_slug}`} target="_blank" rel="noreferrer" className="text-orange-600">view</a></p>
              </div>
              <Link to={`/admin/smart-qr/${b.business_id}`} className="p-2 text-slate-500 hover:text-slate-800" title="Edit" data-testid={`shop-edit-${b.public_slug}`}><Edit className="w-4 h-4" /></Link>
              {b.plan === 'premium' && b.premium_active && <MgmtLinkAction biz={b} />}
              <button onClick={() => toggle(b)} className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 ${b.featured ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-testid={`shop-featured-toggle-${b.public_slug}`}>
                <Star className={`w-3.5 h-3.5 ${b.featured ? 'fill-white' : ''}`} /> {b.featured ? 'Featured' : 'Mark featured'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MgmtLinkAction = ({ biz }) => {
  const [open, setOpen] = useState(false);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const slug = biz.public_slug;

  const fetchLink = async (regenerate = false) => {
    if (regenerate && !window.confirm(`Regenerate the management link for "${biz.business_name}"? The owner's previous link will stop working immediately.`)) return;
    setBusy(true);
    try {
      const url = `${API}/api/smart-qr/admin/businesses/${biz.business_id}/management-link${regenerate ? '/regenerate' : ''}`;
      const res = regenerate ? await axios.post(url, {}, authHeaders()) : await axios.get(url, authHeaders());
      setInfo(res.data); setOpen(true);
      if (regenerate) toast.success('New management link generated — previous link invalidated');
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === 'string' ? d : 'Could not get management link'); }
    setBusy(false);
  };
  const copy = async () => { (await copyText(info.management_url)) ? toast.success(`Management link copied for ${info.business_name}`) : toast.error('Copy failed'); };

  return (
    <>
      <button onClick={() => fetchLink(false)} disabled={busy} title="Copy management link" className="px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 bg-green-100 text-green-800 hover:bg-green-200 disabled:opacity-60" data-testid={`shop-mgmt-link-${slug}`}>
        <KeyRound className="w-3.5 h-3.5" /> Mgmt link
      </button>
      {open && info && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`mgmt-link-modal-${slug}`}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="font-bold text-slate-800 text-sm">Premium management link</p>
            <p className="text-xs text-slate-500 mb-3">{info.business_name} · owner WhatsApp {info.whatsapp}</p>
            <p className="text-xs font-mono bg-slate-50 border border-slate-200 rounded-lg p-2 break-all select-all" data-testid={`mgmt-link-url-${slug}`}>{info.management_url}</p>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button onClick={copy} className="bg-slate-800 text-white py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1" data-testid={`mgmt-link-copy-${slug}`}><Copy className="w-3.5 h-3.5" /> Copy link</button>
              <a href={waLink(info.whatsapp, mgmtShareMsg(info.business_name, info.management_url))} target="_blank" rel="noreferrer" className="bg-[#25D366] text-white py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1" data-testid={`mgmt-link-wa-${slug}`}><MessageCircle className="w-3.5 h-3.5" /> Send to owner</a>
            </div>
            <button onClick={() => fetchLink(true)} disabled={busy} className="mt-2 w-full border border-red-200 text-red-600 py-2 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50 disabled:opacity-60" data-testid={`mgmt-link-regen-${slug}`}><RefreshCw className="w-3.5 h-3.5" /> Regenerate (invalidates old link)</button>
            <button onClick={() => setOpen(false)} className="mt-2 w-full text-xs text-slate-500 hover:text-slate-700" data-testid={`mgmt-link-close-${slug}`}>Close</button>
          </div>
        </div>
      )}
    </>
  );
};
